import pager from './pager.js';
import {DEFAULT_DATASET} from './datasets.js';
import {graneroInstance} from './index.js';

/**
 * Provides methods for working with Granero collections.
 * @module api/collections
 */

// Collections have a "slug id" that is friendly
// for users to use but causes odd edge cases
//  when using the ID as a key.
export const getCollectionId = collection => collection.id.split('-').pop();

/**
 *
 * @param {Object} options
 * @param {number} options.maxItems Limit the result set to this size (by default it equals limit, set to Infinity to load all).
 * @param {function(Array,Boolean,Function)} options.each A function that is called once for
 *     each page of data. The each callback takes 3 arguments. The first is a response object.
 *     The second is a boolean representing whether there are more results, the third is a function that gets
 *     the next page of results. If `each` returns false, it prevents automatic paging.
 *     If the `each` callback is absent, all data will be concatenated and provided when the promise resolves.
 * @returns {Promise<Array>} A promise that resolves when all data is finished
 *     loading or is rejected with any error.  If an `each` callback is not
 *     provided, the promise will resolve with all data concatenated.
 */
export const getCollections = options => {
  const {dataset = DEFAULT_DATASET, params, ...otherOptions} = options || {};

  const config = {
    url: `/ogc/${dataset}/collections/`,
    params: {...params},
    ...otherOptions,
  };

  if (!config.params.limit) {
    config.params.limit = 250; // default so we can bail early on incomplete pages
  }

  if (!config.maxItems) {
    config.maxItems = config.params.limit;
  }

  return pager(config, 'collections', config.each);
};

export const createCollection = (collection, options) => {
  const {dataset = DEFAULT_DATASET, ...config} = options || {};

  if (!collection) {
    throw new Error('Collection object is required');
  }

  return graneroInstance
    .post(`/ogc/${dataset}/collections/`, collection, config)
    .then(res => res.data);
};

export const getCollection = (id, options) => {
  const {dataset = DEFAULT_DATASET, ...config} = options || {};

  return graneroInstance
    .get(`/ogc/${dataset}/collections/${id}/`, config)
    .then(res => res.data);
};

export const updateCollection = (collectionId, updateProps, options) => {
  const {dataset = DEFAULT_DATASET, ...config} = options || {};

  return graneroInstance
    .put(`/ogc/${dataset}/collections/${collectionId}/`, updateProps, config)
    .then(res => res.data);
};

export const deleteCollection = (collectionId, options) => {
  const {dataset = DEFAULT_DATASET, ...config} = options || {};

  return graneroInstance
    .delete(`/ogc/${dataset}/collections/${collectionId}/`, config)
    .then(res => res.data);
};

export const shareCollection = (collectionId, options) => {
  const {dataset = DEFAULT_DATASET, ...config} = options || {};

  return graneroInstance
    .post(`/ogc/${dataset}/collections/${collectionId}/permission/`, {}, config)
    .then(res => res.data);
};

export const unshareCollection = (collectionId, options) => {
  const {dataset = DEFAULT_DATASET, ...config} = options || {};

  return graneroInstance
    .delete(`/ogc/${dataset}/collections/${collectionId}/permission/`, config)
    .then(res => res.data);
};

export const getCollectionPropertyNames = (id, options) => {
  const {dataset = DEFAULT_DATASET, ...config} = options || {};

  return graneroInstance
    .get(`/ogc/${dataset}/collections/${id}/properties`, config)
    .then(res => res.data);
};
